import classnames from 'classnames';
import type React from 'react';
import { useState, type FC } from 'react';

import {
  Popover,
  PopoverLayover,
  PopoverTrigger,
} from '@/core/tamagoshi/Popover/Popover';
import { Text } from '@/core/tamagoshi/Text/Text';
import { Modal } from '@/domains/core/tamagoshi/Modal/Modal';
import { ModalContent } from '@/domains/core/tamagoshi/Modal/ModalContent';
import { ModalHeader } from '@/domains/core/tamagoshi/Modal/ModalHeader';

import styles from './responsivePopover.module.scss';

interface ResponsivePopoverProps {
  className?: string;
  title?: React.ReactNode;
  text: React.ReactNode; // Content of the popover
  trigger: React.ReactNode; // Element that will trigger the popover
  id: string | number; // Unique identifier of the popover
  titleMode?: 'mobile-desktop' | 'mobile' | 'desktop';
  position?: 'top' | 'bottom' | 'left' | 'right';
}

const mobileBreakpoint = 600;

export const ResponsivePopover: FC<ResponsivePopoverProps> = ({
  className,
  id,
  position = 'bottom',
  text,
  title,
  titleMode = 'mobile-desktop',
  trigger,
}) => {
  const [popoverIsVisible, setPopoverIsVisible] = useState(false);
  const [overlayIsVisible, setOverlayIsVisible] = useState(false);

  const onOpenPopover = () => {
    setPopoverIsVisible(true);
  };
  const onClosePopover = () => setPopoverIsVisible(false);

  const onOpenOverlay = (event: React.MouseEvent | React.KeyboardEvent) => {
    if (window.innerWidth < mobileBreakpoint) {
      setOverlayIsVisible(true);
    }
    event.preventDefault();
  };

  const onKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      onOpenOverlay(event);
    }
  };

  const onCloseOverlay = () => {
    setOverlayIsVisible(false);
  };

  if (!text) return null;

  return (
    <>
      <Popover
        position={position}
        fallbackPosition="bottom"
        isVisible={popoverIsVisible}
        onClose={onClosePopover}
        className={styles.popover}
        contentClassName={classnames(className, styles.popoverContent)}
      >
        <PopoverTrigger>
          {({ ref }) => (
            <span
              onMouseEnter={onOpenPopover}
              onMouseLeave={onClosePopover}
              onClick={onOpenOverlay}
              onKeyDown={onKeyDown}
              ref={ref}
              role="button"
              tabIndex={0}
              data-testid={`popoverTrigger${id}`}
            >
              {trigger}
            </span>
          )}
        </PopoverTrigger>
        <PopoverLayover>
          <div>
            {title &&
              (titleMode === 'desktop' || titleMode === 'mobile-desktop') && (
                <Text className={styles.popoverTitle}>{title}</Text>
              )}
            <Text className={styles.popoverText}>{text}</Text>
          </div>
        </PopoverLayover>
      </Popover>

      {overlayIsVisible && (
        <Modal onClose={onCloseOverlay} preventEventPropagation>
          {title &&
            (titleMode === 'mobile' || titleMode === 'mobile-desktop') && (
              <ModalHeader>
                <Text className={styles.overlayTitle}>{title}</Text>
              </ModalHeader>
            )}

          <ModalContent>
            <Text className={styles.overlayText}>{text}</Text>
          </ModalContent>
        </Modal>
      )}
    </>
  );
};
